// src/components/dashboard/logic/useCameraManager.js

import { useState, useMemo } from "react";
import authAxios from "../../../api/authAxios";

export default function useCameraManager(activeOrgId, activeSiteId, organizations = []) {
  // 📹 Source of truth (never filtered)
  const [allCameras, setAllCameras] = useState([]);

  // 📹 UI state
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive
  const [loading, setLoading] = useState(false);

  // 📹 Form / edit state (unchanged)
  const [form, setForm] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editingCameraId, setEditingCameraId] = useState(null);

  // ✅ NEW: Helper to convert org name/ID to numeric ID
  const getNumericOrgId = () => {
    // Already numeric? Return it
    if (typeof activeOrgId === 'number') {
      return activeOrgId;
    }
    
    // Find org by name or ID
    const org = organizations.find(o => 
      o.name === activeOrgId ||
      o.id === activeOrgId ||
      String(o.id) === String(activeOrgId)
    );
    
    console.log('🔄 Org ID conversion:', {
      input: activeOrgId,
      inputType: typeof activeOrgId,
      foundOrg: org?.name,
      numericId: org?.id
    });
    
    return org?.id || activeOrgId;
  };

  // ✅ Fetch cameras (NO org/site guard)
  const fetchCameras = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/cameras/");
      setAllCameras(res.data.results || res.data || []);
    } catch (err) {
      console.error("Failed to fetch cameras:", err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Derived cameras (safe filtering)
  const cameras = useMemo(() => {
    if (statusFilter === "all") return allCameras;

    if (statusFilter === "active") {
      return allCameras.filter(c => c.is_active);
    }

    if (statusFilter === "inactive") {
      return allCameras.filter(c => !c.is_active);
    }

    return allCameras;
  }, [allCameras, statusFilter]);

  // ✅ FIXED: Start editing with numeric org ID
  const startEditing = (camera) => {
    const numericOrgId = getNumericOrgId();
    
    console.log('🔧 Starting edit for camera:', {
      cameraId: camera.id,
      cameraName: camera.name,
      activeSiteId,
      originalOrgId: activeOrgId,
      numericOrgId
    });
    
    setForm({
      ...camera,
      site: activeSiteId,           // Force active site
      organization: numericOrgId,   // ✅ Always numeric
    });
    
    setEditMode(true);
    setEditingCameraId(camera.id);
  };

  // ✅ FIXED: Start adding with numeric org ID
  const startAdding = () => {
    const numericOrgId = getNumericOrgId();
    
    console.log('➕ Starting add camera:', {
      activeSiteId,
      originalOrgId: activeOrgId,
      numericOrgId
    });
    
    setForm({
      site: activeSiteId,           // Pre-fill site
      organization: numericOrgId,   // ✅ Always numeric
      vendor: 'axis',                // Default vendor
      stream_type: 'rtsp',           // Default stream type
    });
    
    setEditMode(false);
    setEditingCameraId(null);
  };

  // ✅ Add / edit camera (FIXED: Use PATCH for edits)
  const handleAddOrEditCamera = async () => {
    try {
      const payload = { ...form };

      const vendor = (payload.vendor || "axis").toLowerCase();
      const streamType = payload.stream_type || "rtsp";

      if (!payload.port) {
        payload.port = vendor === "axis"
          ? (streamType === "mjpeg" ? 80 : 554)
          : 554;
      }

      if (!payload.path || payload.path.trim() === "") {
        if (vendor === "axis") {
          payload.path =
            streamType === "mjpeg"
              ? "/axis-cgi/mjpg/video.cgi"
              : "/axis-media/media.amp";
        } else {
          payload.path = "/";
        }
      }

      if (editMode && editingCameraId) {
        // ✅ FIXED: Use PATCH instead of PUT for partial updates
        console.log("📝 Editing camera:", editingCameraId, payload);
        await authAxios.patch(`/cameras/${editingCameraId}/`, payload);
      } else {
        console.log("➕ Adding camera:", payload);
        await authAxios.post("/cameras/", payload);
      }

      await fetchCameras(); // refresh source of truth
    } catch (err) {
      console.error("❌ Failed to save camera:", err);
      
      // ✅ Better error handling
      if (err.response?.status === 403) {
        const errorMsg = err.response?.data?.detail || "You don't have permission to edit this camera";
        alert(`Permission denied: ${errorMsg}`);
      } else if (err.response?.status === 400) {
        const errorMsg = err.response?.data?.detail || JSON.stringify(err.response?.data);
        alert(`Invalid data: ${errorMsg}`);
      } else {
        alert("Failed to save camera. Check console for details.");
      }
      
      throw err;
    }
  };

  return {
    // cameras to render
    cameras,

    // filtering
    statusFilter,
    setStatusFilter,

    // loading
    loading,

    // CRUD
    fetchCameras,
    handleAddOrEditCamera,

    // form/edit state
    form,
    setForm,
    editMode,
    setEditMode,
    editingCameraId,
    setEditingCameraId,

    // ✅ NEW: Helper functions
    startEditing,    
    startAdding, 
  };
}
